import { Cart, CartImpl } from '@/modules/store/models/Cart';
import { CartItem } from '@/modules/store/models/CartItem';
import { DeliveryWindow } from '@/modules/store/models/DeliveryWindow';
import { PickupWindow } from '@/modules/store/models/PickupWindow';
import CartStorage, { LocalStorageCartStorage } from '@/modules/store/services/CartStorage';

interface AddToCartResult {
  carts: Cart[];
  currentSkuIds: string[];
  nextCartItemId: number;
}

interface CartService {
  addToCart(
    carts: Cart[],
    supplierId: string,
    item: CartItem | CartItem[],
    deliveryWindow?: DeliveryWindow,
    pickupWindow?: PickupWindow,
    nextItemId?: number,
  ): AddToCartResult;
  updateItemFromCart(carts: Cart[], supplierId: string, itemId: number, quantity: number): Cart[];
  removeItemFromCart(carts: Cart[], supplierId: string, itemId: number): Cart[];
  getDeliveryWindow(carts: Cart[], supplierId: string): DeliveryWindow | undefined;
  updateDeliveryWindow(
    carts: Cart[],
    supplierId: string,
    deliveryWindow: DeliveryWindow | null,
  ): Cart[];
  getSkuIds(carts: Cart[]): string[];
  nextItemId(carts: Cart[]): number;
  loadCart(): Promise<Cart[]>;
  storeCart(carts: Cart[]): Promise<void>;
  removeCart(): Promise<void>;
}

class CartServiceImpl implements CartService {
  private readonly storage: CartStorage;

  constructor(storage?: CartStorage) {
    this.storage = storage || new LocalStorageCartStorage();
  }

  addToCart(
    carts: Cart[],
    supplierId: string,
    item: CartItem | CartItem[],
    deliveryWindow?: DeliveryWindow,
    pickupWindow?: PickupWindow,
    nextItemId: number = -1,
  ): AddToCartResult {
    if (nextItemId < 0) {
      nextItemId = this.nextItemId(carts);
    }

    const items = Array.isArray(item) ? item : [item];
    const cartsMap = new Map<string, Cart>();

    carts.forEach((cart) => cartsMap.set(cart.supplierId, cart));

    items.forEach((item: CartItem) => {
      item.id = nextItemId++;

      const existingCart = cartsMap.get(supplierId);

      if (existingCart) {
        existingCart.addItem(item);
        return;
      }

      const newCart = new CartImpl(supplierId, item, 0, deliveryWindow, pickupWindow);

      cartsMap.set(supplierId, newCart);
    });

    const newCarts = Array.from(cartsMap.values());

    const currentSkuIds = Array.from(
      new Set(
        newCarts.flatMap((cart) => {
          return cart.items.map((item) => {
            return item.skuId;
          });
        }),
      ),
    );

    return {
      carts: newCarts,
      currentSkuIds: currentSkuIds,
      nextCartItemId: nextItemId,
    };
  }

  updateItemFromCart(carts: Cart[], supplierId: string, itemId: number, quantity: number): Cart[] {
    return carts.map((cart) => {
      if (cart.supplierId === supplierId) {
        const updatedItems = cart.items.map((item) => {
          if (item.id === itemId) {
            return { ...item, quantity };
          }
          return item;
        });
        return { ...cart, items: updatedItems };
      }
      return cart;
    });
  }

  removeItemFromCart(carts: Cart[], supplierId: string, itemId: number): Cart[] {
    return carts.map((cart) => {
      if (cart.supplierId === supplierId) {
        return {
          ...cart,
          items: cart.items.filter((item) => item.id !== itemId),
        };
      }
      return cart;
    });
  }

  getAllItems(carts: Cart[]): number[] {
    return carts.flatMap((cart) => cart.items.map((item) => item.id));
  }

  async loadCart(): Promise<Cart[]> {
    return this.storage.loadCart();
  }

  async storeCart(carts: Cart[]): Promise<void> {
    return this.storage.storeCart(carts);
  }

  async removeCart(): Promise<void> {
    return this.storage.removeCart();
  }

  getDeliveryWindow(carts: Cart[], supplierId: string): DeliveryWindow | undefined {
    for (const cart of carts) {
      if (cart.supplierId === supplierId) {
        return cart.deliveryWindow;
      }
    }
    return undefined;
  }

  updateDeliveryWindow(
    carts: Cart[],
    supplierId: string,
    deliveryWindow: DeliveryWindow | null,
  ): Cart[] {
    return carts.map((cart) => {
      if (cart.supplierId === supplierId) {
        return {
          ...cart,
          deliveryWindow: deliveryWindow ? { ...deliveryWindow } : undefined,
        };
      }
      return cart;
    });
  }

  getSkuIds(carts: Cart[]): string[] {
    return Array.from(
      new Set(
        carts.flatMap((cart) => {
          return cart.items.map((item) => item.skuId);
        }),
      ),
    );
  }

  nextItemId(carts: Cart[]): number {
    return (
      carts
        .flatMap((cart) => cart.items)
        .reduce((maxId, cartItem) => Math.max(maxId, cartItem.id), 0) + 1
    );
  }
}

export default CartServiceImpl;
